import { Button, Checkbox, Dropdown, Form, Icon, Input, Message, Modal } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, Can, CustomConfirm, useCan } from "components";
import { ROLES, User } from "models";
import "modules/Users/components/EditUserModal/EditUserModal.scss";
import { useDeleteUser, useEditUser } from "modules/Users/components/EditUserModal/hooks";
import { USERS_MODALS, useGetFilterOptions } from "modules/Users/hooks";
import { validateEmail } from "util/common";

type EditUserFormProps = {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  deleteUserModal: boolean;
  handleCloseModal: (modalName: USERS_MODALS) => void;
  handleOpenModal: (modalName: USERS_MODALS) => void;
};

export const EditUserForm = ({ isOpen, onClose, user, deleteUserModal, handleCloseModal, handleOpenModal }: EditUserFormProps) => {
  const { roleOptions } = useGetFilterOptions();

  const canUpdateUsers = useCan("update", "users");
  const { handleDeleteUser, handleDeleteUserClick, isDeleteLoading } = useDeleteUser({ handleCloseModal, handleOpenModal, onClose, user });
  const { email, formSubmitted, handleEmailChange, handleUpdateUser, handleUserStatusChange, isFormSubmitting, role, isMissingValue, setRole, status } = useEditUser({
    isOpen,
    onClose,
    user
  });

  return (
    <>
      <Modal className="EditUserModal" size="tiny" closeOnDimmerClick open={isOpen} onClose={onClose}>
        <Modal.Header className="EditUserModal__modal-header">
          <div className="EditUserModal__modal-header">
            <p>Edit User</p>
          </div>

          <div className="EditUserModal__checkbox-container">
            <Checkbox checked={status} onChange={(_, { checked }) => handleUserStatusChange(!!checked)} label="Active" />
          </div>
        </Modal.Header>
        <Modal.Content>
          <Form style={{ padding: "0 7px" }}>
            <Form.Group>
              <Form.Field id="email" width={16} required>
                <label>Email</label>
                <Input value={email} fluid name="email" onChange={handleEmailChange} placeholder="Email" />
              </Form.Field>
            </Form.Group>
            <Form.Group style={{ marginBottom: "0" }}>
              <Form.Field id="role" width={16} required>
                <label>Role</label>
                <Dropdown
                  selection
                  selectOnBlur={false}
                  options={roleOptions}
                  placeholder="Select Role"
                  value={Number(role)}
                  onChange={(_, { value }) => setRole(value as ROLES)}
                />
              </Form.Field>
            </Form.Group>
          </Form>
          {(formSubmitted && isMissingValue()) || (email && !validateEmail(email)) ? (
            <Message error content={formSubmitted && isMissingValue() ? "All fields are required" : "Invalid email"} />
          ) : null}
        </Modal.Content>
        <Modal.Actions>
          <Can I="delete" the="users">
            <Button onClick={handleDeleteUserClick} color="red" className="EditUserModal__delete-button">
              Delete
              <Icon className="trash" />
            </Button>
          </Can>

          <div className="EditUserModal__modal-footer">
            <Button onClick={onClose} basic>
              Cancel
              <Icon className="xmark" />
            </Button>
            <Button color="green" disabled={isFormSubmitting || !canUpdateUsers} loading={isFormSubmitting} type="button" onClick={handleUpdateUser}>
              Save
              <Icon className="check" />
            </Button>
          </div>
        </Modal.Actions>
      </Modal>

      <CustomConfirm
        isOpen={deleteUserModal}
        isLoading={isDeleteLoading}
        type={CUSTOM_CONFIRM_TYPES.Danger}
        handleConfirm={handleDeleteUser}
        handleCancel={() => handleCloseModal(USERS_MODALS.DeleteUserModal)}
        customTitle="Are you sure?"
        confirmMsg="Are you sure that you want to delete this user? You can’t undo this action."
      />
    </>
  );
};
